import Phaser from 'phaser';

/**
 * Armory-tier hero frame ornament.
 *
 * Renders escalating decorative flourishes over a hero portrait frame based on
 * the hero's Armory level. Each tier is additive — tier 4 draws everything
 * tier 1/2/3 would, plus its own additions. Pure Phaser `Graphics`, no
 * textures required. One extra tween is registered at tier ≥ 6 for a slow
 * shimmer pulse; callers should add the returned `tweens` array to their
 * cleanup so the tween is stopped when the scene tears down.
 *
 * Call AFTER drawing the base frame — this layer sits on top of the portrait's
 * existing bronze border / corner brackets, adding premium detailing as the
 * player invests in the hero.
 *
 * Tiers (from `armoryFrameTier` in shared/logic/armory.ts):
 *   0  < Lv 10   baseline (nothing drawn)
 *   1  Lv 10     gold pips at corners
 *   2  Lv 20     inset secondary border (nested frame)
 *   3  Lv 30     small gem dot in each corner
 *   4  Lv 50     runic sigil etched top edge
 *   5  Lv 100    laurel arches top + bottom
 *   6  Lv 200    slow-pulse gold shimmer on the border
 *   7  Lv 500    outer golden glow halo
 *   8  Lv 1000   chromatic edge + moving particle motes
 */
export interface HeroFrameOrnamentResult {
  graphics: Phaser.GameObjects.Graphics[];
  tweens: Phaser.Tweens.Tween[];
}

/**
 * Phaser-drawn ornamental flourish bracketing a title text. Two symmetric
 * filigrees on either side of the label — a short horizontal rule with gem pips
 * at either end. Designed to make plain centred header text feel less bare
 * without adding any new textures. Returns the created Graphics so callers can
 * dispose of them on scene teardown.
 *
 * @param cx centre-x of the title text (flourishes are mirrored around it)
 * @param cy baseline y of the title (flourishes are vertically centred on it)
 * @param textWidth approx rendered width of the title so flourishes sit just
 *                  outside the glyphs instead of overlapping them
 * @param color stroke colour for the filigree (defaults to bronze palette)
 */
export function drawTitleFlourish(
  scene: Phaser.Scene,
  cx: number, cy: number,
  textWidth: number,
  depth: number,
  color = 0xc8973a,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics().setDepth(depth);
  const pad = 14; // gap between glyphs and flourish start
  const ruleLen = 34; // how far each flourish reaches outward
  const left = cx - textWidth / 2 - pad;
  const right = cx + textWidth / 2 + pad;

  g.lineStyle(1.25, color, 0.9);
  // Left flourish: pip · tapered line · pip · small chevron pointing at title
  g.fillStyle(color, 0.95);
  g.fillCircle(left - ruleLen, cy, 1.8);
  g.beginPath();
  g.moveTo(left - ruleLen + 4, cy);
  g.lineTo(left - 4, cy);
  g.strokePath();
  g.fillCircle(left - 4, cy, 1.4);
  // Chevron tip ( < ) right before the title
  g.beginPath();
  g.moveTo(left - 2, cy - 3);
  g.lineTo(left + 2, cy);
  g.lineTo(left - 2, cy + 3);
  g.strokePath();

  // Right flourish: mirror
  g.fillCircle(right + ruleLen, cy, 1.8);
  g.beginPath();
  g.moveTo(right + 4, cy);
  g.lineTo(right + ruleLen - 4, cy);
  g.strokePath();
  g.fillCircle(right + 4, cy, 1.4);
  g.beginPath();
  g.moveTo(right + 2, cy - 3);
  g.lineTo(right - 2, cy);
  g.lineTo(right + 2, cy + 3);
  g.strokePath();

  return g;
}

export function drawHeroFrameOrnament(
  scene: Phaser.Scene,
  centerX: number, centerY: number,
  frameW: number, frameH: number,
  tier: number,
  depth: number,
): HeroFrameOrnamentResult {
  const out: HeroFrameOrnamentResult = { graphics: [], tweens: [] };
  if (tier <= 0) return out;

  const L = centerX - frameW / 2;
  const T = centerY - frameH / 2;
  const R = centerX + frameW / 2;
  const B = centerY + frameH / 2;

  // ---- Tier 1 — gold corner pips ----
  {
    const g = scene.add.graphics().setDepth(depth);
    g.fillStyle(0xf0c040, 0.95);
    const pipR = 1.6;
    g.fillCircle(L + 3, T + 3, pipR);
    g.fillCircle(R - 3, T + 3, pipR);
    g.fillCircle(L + 3, B - 3, pipR);
    g.fillCircle(R - 3, B - 3, pipR);
    out.graphics.push(g);
  }

  if (tier >= 2) {
    // Nested inner border — 2 px in from the base frame.
    const g = scene.add.graphics().setDepth(depth);
    g.lineStyle(1, 0xb98a35, 0.75);
    g.strokeRect(L + 2, T + 2, frameW - 4, frameH - 4);
    out.graphics.push(g);
  }

  if (tier >= 3) {
    // Gem dots inside the brackets.
    const g = scene.add.graphics().setDepth(depth);
    g.fillStyle(0xff6a40, 0.9);
    g.fillCircle(L + 5, T + 5, 1.2);
    g.fillCircle(R - 5, T + 5, 1.2);
    g.fillCircle(L + 5, B - 5, 1.2);
    g.fillCircle(R - 5, B - 5, 1.2);
    out.graphics.push(g);
  }

  if (tier >= 4) {
    // Runic sigil etching across the top edge — five small marks.
    const g = scene.add.graphics().setDepth(depth);
    g.lineStyle(1, 0xe8c060, 0.75);
    const runeY = T + 1.5;
    const startX = L + frameW * 0.22;
    const endX = L + frameW * 0.78;
    const step = (endX - startX) / 4;
    for (let i = 0; i <= 4; i++) {
      const rx = startX + i * step;
      g.beginPath();
      g.moveTo(rx, runeY);
      g.lineTo(rx, runeY + 2);
      if (i % 2 === 0) {
        g.moveTo(rx - 1.5, runeY + 1);
        g.lineTo(rx + 1.5, runeY + 1);
      }
      g.strokePath();
    }
    out.graphics.push(g);
  }

  if (tier >= 5) {
    // Laurel arches at top + bottom center — small curved strokes.
    const g = scene.add.graphics().setDepth(depth);
    g.lineStyle(1.25, 0xfbd46a, 0.85);
    const archW = Math.min(18, frameW * 0.35);
    g.beginPath();
    g.arc(centerX, T - 2, archW / 2, Math.PI, Math.PI * 2, false);
    g.strokePath();
    g.beginPath();
    g.arc(centerX, B + 2, archW / 2, 0, Math.PI, false);
    g.strokePath();
    out.graphics.push(g);
  }

  if (tier >= 6) {
    // Slow shimmer pulse — draw a soft gold overlay border and tween its alpha.
    const g = scene.add.graphics().setDepth(depth);
    g.lineStyle(2, 0xffd060, 0.55);
    g.strokeRect(L, T, frameW, frameH);
    out.graphics.push(g);
    const tween = scene.tweens.add({
      targets: g, alpha: { from: 0.35, to: 0.9 },
      duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    out.tweens.push(tween);
  }

  if (tier >= 7) {
    // Outer golden glow halo — a larger semi-transparent border outside the frame.
    const g = scene.add.graphics().setDepth(depth - 0.05);
    g.lineStyle(3, 0xffb020, 0.22);
    g.strokeRect(L - 3, T - 3, frameW + 6, frameH + 6);
    g.lineStyle(2, 0xffd060, 0.45);
    g.strokeRect(L - 1, T - 1, frameW + 2, frameH + 2);
    out.graphics.push(g);
  }

  if (tier >= 8) {
    // Chromatic edge + two slow-moving particle motes that orbit the frame.
    const g = scene.add.graphics().setDepth(depth);
    const palette = [0xff60b0, 0xffd060, 0x60ffd0];
    for (let i = 0; i < 3; i++) {
      g.lineStyle(1, palette[i], 0.45);
      g.strokeRect(L - (i + 1), T - (i + 1), frameW + (i + 1) * 2, frameH + (i + 1) * 2);
    }
    out.graphics.push(g);
    // Two motes — one slides along the top, one along the bottom.
    for (let m = 0; m < 2; m++) {
      const startX = m === 0 ? L : R;
      const endX   = m === 0 ? R : L;
      const yOff   = m === 0 ? T - 2 : B + 2;
      const mote = scene.add.circle(startX, yOff, 1.6, 0xffe060, 1).setDepth(depth + 0.1);
      out.graphics.push(mote as unknown as Phaser.GameObjects.Graphics);
      const tween = scene.tweens.add({
        targets: mote, x: endX, duration: 2600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
      out.tweens.push(tween);
    }
  }

  return out;
}
